import { apiClient } from './api';

const BASE = '/api/notifications';
export const NOTIFICATION_UNREAD_EVENT = 'apparify:notification-unread-count';

export const publishUnreadCount = (count) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(NOTIFICATION_UNREAD_EVENT, {
    detail: { count: Math.max(0, Number(count) || 0) },
  }));
};

/* ══════════════════════════════════════════════
   Inbox
   ══════════════════════════════════════════════ */

/**
 * Fetch paginated notifications.
 * @param {{ page?: number, limit?: number, filter?: 'all'|'unread', category?: string }} params
 */
export const getNotifications = async (params = {}) => {
  const { data } = await apiClient.get(BASE, { params });
  return data; // { items, meta }
};

/** Get unread badge count. */
export const getUnreadCount = async () => {
  const { data } = await apiClient.get(`${BASE}/unread-count`);
  return data.count;
};

/** Mark a single notification as read. */
export const markAsRead = async (id) => {
  const { data } = await apiClient.patch(`${BASE}/${id}/read`);
  return data;
};

/** Mark ALL unread notifications as read. */
export const markAllAsRead = async () => {
  const { data } = await apiClient.patch(`${BASE}/read-all`);
  return data;
};

/** Soft-delete a single notification. */
export const deleteNotification = async (id) => {
  const { data } = await apiClient.delete(`${BASE}/${id}`);
  return data;
};

/* ══════════════════════════════════════════════
   Settings
   ══════════════════════════════════════════════ */

/** Fetch both preferences + global settings in one call. */
export const getSettings = async () => {
  const { data } = await apiClient.get(`${BASE}/settings`);
  return data; // { preferences, global }
};

/** Bulk-update per-category/channel preferences. */
export const updatePreferences = async (preferences) => {
  const { data } = await apiClient.patch(`${BASE}/settings/preferences`, { preferences });
  return data; // { preferences }
};

/** Update global settings (digest, quiet hours, cap). */
export const updateGlobalSettings = async (settings) => {
  const { data } = await apiClient.patch(`${BASE}/settings/global`, settings);
  return data; // { global }
};

/* ══════════════════════════════════════════════
   SSE helpers
   ══════════════════════════════════════════════ */

/* ══════════════════════════════════════════════
   Notification Actions (event-driven)
   ══════════════════════════════════════════════ */

/** Fetch action instances for a specific notification. */
export const getNotificationActions = async (notificationId) => {
  const { data } = await apiClient.get(`${BASE}/${notificationId}/actions`);
  return data.actions;
};

/** Execute a notification action instance (accept, decline, cancel, etc.). */
export const executeAction = async (instanceId, payload) => {
  const { data } = await apiClient.post(`${BASE}/actions/${instanceId}/execute`, payload || {});
  return data;
};

/* ══════════════════════════════════════════════
   Invitation actions (account-side business invitations)
   ══════════════════════════════════════════════ */

/** Decline a business invitation from the account-side inbox. */
export const declineInvitation = async (invitationId) => {
  const { data } = await apiClient.patch(
    `/api/businesses/invitations/${invitationId}/decline`,
    {},
    { headers: { 'X-Portal': 'shop' } },
  );
  return data;
};

/**
 * Check the current status of an invitation.
 * Used to determine if an invitation was already acted on from the account side.
 */
export const getInvitationStatus = async (invitationId) => {
  const { data } = await apiClient.get(
    `/api/businesses/invitations/${invitationId}/status`,
    { headers: { 'X-Portal': 'shop' } },
  );
  return data;
};

/* ══════════════════════════════════════════════
   Business application actions (cancel)
   ══════════════════════════════════════════════ */

/** Cancel a pending business application. */
export const cancelBusinessApplication = async (applicationId) => {
  const { data } = await apiClient.patch(
    `/api/businesses/applications/${applicationId}/cancel`,
    {},
    { headers: { 'X-Portal': 'shop' } },
  );
  return data;
};

/* ══════════════════════════════════════════════
   Appeal actions
   ══════════════════════════════════════════════ */

/** Submit a suspension appeal for a business. */
export const submitAppeal = async (businessId, reason) => {
  const { data } = await apiClient.post(
    `/api/businesses/${businessId}/appeals`,
    { reason },
    { headers: { 'X-Portal': 'shop' } },
  );
  return data;
};

/** Get active suspension info for a business. */
export const getActiveSuspension = async (businessId) => {
  const { data } = await apiClient.get(
    `/api/businesses/${businessId}/suspension`,
    { headers: { 'X-Portal': 'shop' } },
  );
  return data;
};

/** Get user's own appeal history. */
export const getMyAppeals = async () => {
  const { data } = await apiClient.get('/api/businesses/appeals/mine', {
    headers: { 'X-Portal': 'shop' },
  });
  return data;
};

/* ══════════════════════════════════════════════
   Resubmit application
   ══════════════════════════════════════════════ */

/** Get application data for resubmit (prefilled). */
export const getApplicationForEdit = async (applicationId) => {
  const { data } = await apiClient.get(
    `/api/businesses/applications/${applicationId}/edit`,
    { headers: { 'X-Portal': 'shop' } },
  );
  return data;
};

/** Get current user's business applications (status + audit context). */
export const getMyApplications = async () => {
  const { data } = await apiClient.get('/api/businesses/applications', {
    headers: { 'X-Portal': 'shop' },
  });
  return data;
};

/** Resubmit an application with updated fields + files. */
export const resubmitApplication = async (applicationId, formData) => {
  const { data } = await apiClient.put(
    `/api/businesses/applications/${applicationId}/resubmit`,
    formData,
    { headers: { 'X-Portal': 'shop' } },
  );
  return data;
};

/** Get the immutable event timeline for an application. */
export const getApplicationTimeline = async (applicationId) => {
  const { data } = await apiClient.get(
    `/api/businesses/applications/${applicationId}/timeline`,
    { headers: { 'X-Portal': 'shop' } },
  );
  return data;
};
